import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CalendarDays,
  CalendarRange,
  Clock,
  FileCheck2,
  LayoutDashboard,
  LogOut,
  MapPinned,
  Settings,
  UserCircle2,
  UserCog,
  Users,
} from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  SidebarFooter, SidebarHeader, useSidebar,
} from "@/components/ui/sidebar";
import { GeoKerjaLogo } from "@/components/GeoKerjaLogo";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/LocaleContext";
import { cn } from "@/lib/utils";

const mainItems = [
  { key: "nav.dashboard", url: "/", icon: LayoutDashboard, end: true },
  { key: "nav.attendance", url: "/attendance", icon: Activity },
  { key: "nav.employees", url: "/employees", icon: Users },
  { key: "nav.departments", url: "/departments", icon: Building2 },
  { key: "nav.users", url: "/users", icon: UserCog },
  { key: "nav.requests", url: "/requests", icon: FileCheck2 },
  { key: "nav.sites", url: "/sites", icon: MapPinned },
  { key: "nav.shifts", url: "/shifts", icon: Clock },
  { key: "nav.roster", url: "/roster", icon: CalendarRange },
  { key: "nav.holidays", url: "/holidays", icon: CalendarDays },
  { key: "nav.reports", url: "/reports", icon: BarChart3 },
];

const supportItems = [
  { key: "nav.aiAssistant", url: "/assistant", icon: Bot },
  { key: "nav.notifications", url: "/notifications", icon: Bell },
  { key: "nav.settings", url: "/settings", icon: Settings },
  { key: "nav.profile", url: "/profile", icon: UserCircle2 },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const { user, logout } = useAuth();
  const t = useT();
  const collapsed = state === "collapsed";
  const location = useLocation();

  // Scoped admins operate inside one org subtree — tenant-wide areas are
  // blocked server-side (403), so don't offer them in the nav.
  const hiddenWhenScoped = new Set(["/users", "/settings"]);
  const visible = (items: typeof mainItems) =>
    user?.scoped ? items.filter((i) => !hiddenWhenScoped.has(i.url)) : items;

  const renderItem = (item: typeof mainItems[number]) => {
    const isActive = item.end ? location.pathname === item.url : location.pathname.startsWith(item.url);
    const label = t(item.key);
    return (
      <SidebarMenuItem key={item.key}>
        <SidebarMenuButton asChild tooltip={label} isActive={isActive}>
          <RouterNavLink
            to={item.url}
            end={item.end}
            className={cn(
              "group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors",
              isActive
                ? "bg-sidebar-primary/15 text-white font-medium"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-white",
            )}
          >
            <item.icon className={cn("h-4 w-4 shrink-0", isActive && "text-sidebar-primary")} />
            {!collapsed && <span className="truncate">{label}</span>}
            {isActive && !collapsed && (
              <span className="ml-auto h-1.5 w-1.5 rounded-full bg-sidebar-primary" />
            )}
          </RouterNavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border bg-sidebar px-3 py-4">
        {collapsed ? (
          <div className="flex justify-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-primary">
              <span className="font-display text-sm font-bold text-white">G</span>
            </div>
          </div>
        ) : (
          <GeoKerjaLogo variant="light" subtitle="Admin Portal" />
        )}
      </SidebarHeader>

      <SidebarContent className="bg-sidebar px-2 py-3">
        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-sidebar-foreground/50">Workforce</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>{visible(mainItems).map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          {!collapsed && <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-sidebar-foreground/50">Operations</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>{visible(supportItems).map(renderItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border bg-sidebar p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 rounded-lg p-2 hover:bg-sidebar-accent transition-colors">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-xs font-semibold text-white">
              {user?.avatarInitials ?? "AD"}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-xs font-medium text-white">{user?.name}</div>
              <div className="truncate text-[10px] text-sidebar-foreground/60">{user?.role}</div>
            </div>
            <button onClick={logout} className="rounded p-1.5 text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-white transition-colors" title="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button onClick={logout} className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-gradient-primary text-xs font-semibold text-white" title="Logout">
            {user?.avatarInitials ?? "AD"}
          </button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
